/**
 * Soft-safe street process timeline (light → heavy review depth).
 * Pure: HandState → ordered street segments for Share / Copy / PNG.
 *
 * Ambition beyond Soft14 ops: denser process chronology that spans
 * light (preflop glance) → heavy (multi-street river depth) without
 * inventing empty streets or solver metrics.
 *
 * Soft-refuse: no GTO / equity% / outs / win-rate / fabricated empties.
 */
import type { Card, HandState, Street, StreetAction } from './types'
import { groupActionsByStreet } from './shareHuman'

const STREET_ORDER: Street[] = ['preflop', 'flop', 'turn', 'river']

/** Review weight: early streets light; later / denser streets heavier. */
export type TimelineWeight = 'light' | 'mid' | 'heavy'

export type ProcessTimelineSegment = {
  street: Street
  /** Actions recorded on this street (may be empty if board-only segment). */
  actions: StreetAction[]
  /** Board cards revealed for this street (flop 3 / turn 1 / river 1). */
  board: Card[]
  weight: TimelineWeight
  /** Action count — honesty metric for density, never equity. */
  actionCount: number
}

export type ProcessTimeline = {
  /** Filled streets only — omit empties (never fabricate). */
  segments: ProcessTimelineSegment[]
  /** Heaviest weight present (for export depth cues). */
  peakWeight: TimelineWeight | null
  /** Total actions across timeline. */
  totalActions: number
}

function boardForStreet(state: HandState, street: Street): Card[] {
  if (street === 'flop') {
    return state.board.flop.filter((c): c is Card => Boolean(c))
  }
  if (street === 'turn') {
    return state.board.turn ? [state.board.turn] : []
  }
  if (street === 'river') {
    return state.board.river ? [state.board.river] : []
  }
  return []
}

function weightFor(street: Street, actionCount: number): TimelineWeight {
  // Street progression is the primary light→heavy axis; density nudges up.
  const streetBase: Record<Street, TimelineWeight> = {
    preflop: 'light',
    flop: 'mid',
    turn: 'mid',
    river: 'heavy',
  }
  let w = streetBase[street]
  if (actionCount >= 4 && w === 'light') w = 'mid'
  if (actionCount >= 3 && w === 'mid') w = 'heavy'
  if (actionCount >= 5) w = 'heavy'
  return w
}

const WEIGHT_RANK: Record<TimelineWeight, number> = {
  light: 0,
  mid: 1,
  heavy: 2,
}

function maxWeight(a: TimelineWeight, b: TimelineWeight): TimelineWeight {
  return WEIGHT_RANK[a] >= WEIGHT_RANK[b] ? a : b
}

/**
 * Build denser street process timeline.
 * Includes a street only when it has actions and/or board cards.
 */
export function buildProcessTimeline(state: HandState): ProcessTimeline {
  const byStreet = groupActionsByStreet(state.actions)
  const actionMap = new Map(byStreet.map((g) => [g.street, g.items]))

  const segments: ProcessTimelineSegment[] = []
  for (const street of STREET_ORDER) {
    const actions = actionMap.get(street) ?? []
    const board = boardForStreet(state, street)
    if (actions.length === 0 && board.length === 0) continue
    // Flop board alone without flop actions still belongs in process review
    // when later streets exist or flop cards were dealt.
    const actionCount = actions.length
    segments.push({
      street,
      actions,
      board,
      weight: weightFor(street, actionCount),
      actionCount,
    })
  }

  let peakWeight: TimelineWeight | null = null
  let totalActions = 0
  for (const s of segments) {
    totalActions += s.actionCount
    peakWeight = peakWeight ? maxWeight(peakWeight, s.weight) : s.weight
  }

  return { segments, peakWeight, totalActions }
}

/**
 * Export-depth chronology lines for Copy / PNG denser process review.
 * Callers supply street headers + verbs (i18n stays outside).
 * Soft-refuse: no empty fabricated streets; no solver tokens.
 */
export function timelineExportLines(
  timeline: ProcessTimeline,
  opts: {
    streetHeader: (street: Street) => string
    actionVerb: (type: StreetAction['type']) => string
    boardLabel: (cards: string) => string
    formatCards: (cards: Card[]) => string
    bullet?: string
  },
): string[] {
  if (timeline.segments.length === 0) return []
  const bullet = opts.bullet ?? '· '
  const lines: string[] = []
  for (const seg of timeline.segments) {
    if (lines.length) lines.push('')
    lines.push(opts.streetHeader(seg.street))
    if (seg.board.length) {
      const cards = opts.formatCards(seg.board)
      if (cards) lines.push(opts.boardLabel(cards))
    }
    for (const a of seg.actions) {
      const size = a.size ? ` ${a.size}` : ''
      lines.push(
        `${bullet}${a.actor}: ${opts.actionVerb(a.type)}${size}`,
      )
    }
  }
  return lines
}
